const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const UserSession = require('./UserSession');

/**
 * @constructor
 *
 * @param {DataStore} dataStore
 * @param {Hydrator} hydrator
 */
const UserSessionManager = function (
    dataStore,
    hydrator,
) {
    this.dataStore = dataStore;
    this.hydrator = hydrator;
};

UserSessionManager.prototype = {
    /**
     * Return all user sessions.
     *
     * @return {Promise<UserSession[]>}
     */
    getAll() {
        return this.dataStore
            .findAll(DataStoreNames.USER_SESSIONS)
            .then(this.hydrator.createModelHydrator(UserSession));
    },

    /**
     * Get an existing user session from the DB.
     * Resolves either with the user session or with null.
     *
     * @param {number} userID
     *
     * @return {Promise<?UserSession>}
     */
    getSession(userID) {
        return new Promise((resolve) => {
            this.dataStore
                .find(DataStoreNames.USER_SESSIONS, userID)
                .then((result) => {
                    if (result) {
                        let userSession = this.hydrator.hydrate(
                            result,
                            new UserSession()
                        );
                        resolve(userSession);
                    } else {
                        resolve(null);
                    }
                })
                .catch(
                    /* istanbul ignore next */
                    () => resolve(null)
                );
        });
    },

    /**
     * Loads a user session for the given user, or creates a new one if it doesn't exist.
     *
     * @param {User} user
     *
     * @returns {Promise<UserSession>}
     */
    async getOrCreateSession(user) {
        let session = await this.getSession(user.ID);
        if (session) {
            return session;
        }

        session = new UserSession({
            apiKey: user.apiKey,
            ID: user.ID,
            username: user.username,
        });

        await this.persist(session);

        return session;
    },

    /**
     * Save a session in the DB.
     *
     * @param {UserSession} userSession
     */
    persist(userSession) {
        return this.dataStore.persist(
            DataStoreNames.USER_SESSIONS,
            userSession
        );
    },

    /**
     * API keys (and formerly passwords) are stored in the user sessions so that we can
     * switch between users with just a PIN. But this should only be possible if the
     * user has logged in recently (last 24 hours).
     * This method clears stored API keys for users that logged in over 24 hours ago.
     *
     * @param {Date} [cutoff] Cutoff can be passed in for testing.
     *
     * @return {Promise<UserSession[]>}
     */
    pruneOldAuthData(cutoff) {
        if (!cutoff) {
            cutoff = new Date();
            cutoff.setTime(cutoff.getTime() - (86400 * 1000));
        }

        return this.getAll()
            .then(
                /**
                 * @param {UserSession[]} sessions
                 */
                async (sessions) => {
                    let modified = [];

                    for (let i = 0; i < sessions.length; i++) {
                        let session = sessions[i];
                        if (!session.lastLoginDatetime || session.lastLoginDatetime < cutoff) {
                            session.apiKey = null;
                            await this.persist(session);
                            modified.push(session);
                        }
                    }

                    return modified;
                }
            );
    },

    /**
     * Returns the page the user A was on before using the 'switch user' function
     * to change to user B.
     * Removes the saved value after resolving.
     *
     * @param {number} userID
     *
     * @return {Promise<?string>}
     */
    pullLastSessionScreen(userID) {
        return new Promise((resolve) => {
            this.getSession(userID)
                .then(async (session) => {
                    if (session && session.lastScreen) {
                        const lastScreen = session.lastScreen;

                        // Remove the lastScreen saved in the session.
                        session.lastScreen = null;
                        await this.persist(session);

                        resolve(lastScreen);
                    } else {
                        resolve(null);
                    }
                })
                .catch(
                    /* istanbul ignore next */
                    () => resolve(null)
                );
        });
    },

    /**
     * Returns every currently stashed cart, from every EPOS user.
     *
     * @return {Promise<StashedCart[]>|Promise<StashedCart[]>}
     */
    getStashedCarts() {
        return this.getAll()
            .then((sessions) => sessions.reduce(
                /**
                 * @param {[]} allCarts
                 * @param {UserSession} session
                 */
                (allCarts, session) => allCarts.concat(session.stashedCarts),
                []
            ));
    },

    /**
     * @param {string[]} thirdPartyRefs
     *
     * @return {Promise<boolean>}
     */
    deleteStashedCartsByThirdPartyRefs(thirdPartyRefs) {
        return this.getAll()
            .then(
                /**
                 * @param {UserSession[]} sessions
                 */
                async (sessions) => {
                    let removed = false;
                    let persistPromises = [];

                    sessions.forEach((session) => {
                        let removedInThisSession = false;
                        thirdPartyRefs.forEach((thirdPartyRef) => {
                            if (session.deleteStashedCartByThirdPartyRef(thirdPartyRef)) {
                                removedInThisSession = true;
                                removed = true;
                            }
                        });

                        if (removedInThisSession) {
                            // This session contained the stashed cart and it has now been removed.
                            // Re-save this session.
                            persistPromises.push(
                                this.persist(session)
                            );
                        }
                    });

                    return Promise.all(persistPromises)
                        .then(() => removed);
                }
            );
    },

    deleteStashedCartByThirdPartyRef(thirdPartyRef) {
        return this.deleteStashedCartsByThirdPartyRefs([thirdPartyRef]);
    },

    /**
     * @return {Promise}
     */
    unlockAll() {
        return this.getAll()
            .then((userSessions) => {
                return userSessions.map((userSession) => {
                    userSession.isLocked = false;
                    return this.persist(userSession);
                });
            });
    },
};

module.exports = UserSessionManager;
